import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  IndianRupee, Search, CreditCard, Upload, Eye, RefreshCw, ChevronDown, ChevronUp, Image
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountsPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [accountTotals, setAccountTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClient, setExpandedClient] = useState(null);
  const [clientPayments, setClientPayments] = useState({});

  // Payment dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', mode: 'cash', notes: '', received_date: '' });
  const [screenshotFile, setScreenshotFile] = useState(null);

  const isManager = (user?.roles || [user?.role]).some(r => ['manager'].includes(r));
  const canView = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  useEffect(() => { fetchAccounts(); fetchAccountTotals(); }, []);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/accounts');
      setAccounts(res.data);
    } catch (err) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTotals = async () => {
    try {
      const res = await api.get('/accounts/by-account');
      setAccountTotals(res.data || []);
    } catch (err) { /* non-fatal */ }
  };

  const fetchPayments = async (clientId) => {
    try {
      const res = await api.get(`/accounts/${clientId}/payments`);
      setClientPayments(prev => ({ ...prev, [clientId]: res.data }));
    } catch (err) {
      toast.error('Failed to load payments');
    }
  };

  const toggleExpand = (clientId) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      if (!clientPayments[clientId]) {
        fetchPayments(clientId);
      }
    }
  };

  const openPaymentDialog = (account) => {
    setSelectedClient(account);
    setPaymentForm({ amount: '', mode: 'upi', account: '', notes: '', received_date: new Date().toISOString().split('T')[0] });
    setScreenshotFile(null);
    setShowPaymentDialog(true);
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    try {
      const res = await api.post(`/accounts/${selectedClient.client_id}/payments`, {
        amount: parseFloat(paymentForm.amount),
        mode: paymentForm.mode,
        account: paymentForm.account || null,
        notes: paymentForm.notes,
        received_date: paymentForm.received_date ? new Date(paymentForm.received_date).toISOString() : null,
      });

      if (screenshotFile) {
        const formData = new FormData();
        formData.append('file', screenshotFile);
        await api.post(`/accounts/${selectedClient.client_id}/payments/${res.data.id}/screenshot`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      fetchAccounts();
      fetchAccountTotals();
      fetchPayments(selectedClient.client_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const filtered = accounts.filter(a =>
    a.client_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAmount = filtered.reduce((s, a) => s + a.total_amount, 0);
  const totalPaid = filtered.reduce((s, a) => s + a.total_paid, 0);
  const totalBalance = filtered.reduce((s, a) => s + a.balance, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Accounts</h1>
          <p className="text-zinc-500 text-sm mt-1">{accounts.length} clients with projects</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAccounts}
          className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-accounts">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Project Value</p>
            <p className="text-2xl font-bold text-white mt-1" data-testid="total-amount">
              &#8377;{totalAmount.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Received</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="total-paid">
              &#8377;{totalPaid.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Balance</p>
            <p className="text-2xl font-bold text-amber-400 mt-1" data-testid="total-balance">
              &#8377;{totalBalance.toLocaleString('en-IN')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Account Totals Strip */}
      {accountTotals.length > 0 && (
        <div className="bg-[#121212] border border-white/10 rounded-lg p-4" data-testid="by-account-strip">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Received By Account</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {accountTotals.map(a => (
              <div key={a.account}
                className={`rounded-lg p-3 border ${
                  a.account === 'Unassigned'
                    ? 'bg-[#0A0A0A] border-white/5'
                    : 'bg-[#F5A623]/5 border-[#F5A623]/20'
                }`}
                data-testid={`account-total-${a.account}`}>
                <p className={`text-xs uppercase tracking-wider ${a.account === 'Unassigned' ? 'text-zinc-500' : 'text-[#F5A623]'}`}>{a.account}</p>
                <p className="text-xl font-bold text-white mt-1">&#8377;{(a.total || 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{a.count} payment{a.count !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <Input placeholder="Search clients..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500" data-testid="search-accounts" />
      </div>

      {/* Client Table */}
      <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full" data-testid="accounts-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Client</th>
              <th className="text-left text-xs text-zinc-400 font-medium px-4 py-3">Projects</th>
              <th className="text-right text-xs text-zinc-400 font-medium px-4 py-3">Total</th>
              <th className="text-right text-xs text-zinc-400 font-medium px-4 py-3">Paid</th>
              <th className="text-right text-xs text-zinc-400 font-medium px-4 py-3">Balance</th>
              <th className="text-center text-xs text-zinc-400 font-medium px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(account => (
              <React.Fragment key={account.client_id}>
                <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  onClick={() => toggleExpand(account.client_id)} data-testid={`account-row-${account.client_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {expandedClient === account.client_id ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
                      <div>
                        <p className="text-white text-sm font-medium">{account.client_name}</p>
                        <p className="text-[11px] text-zinc-500">{account.client_phone}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {account.projects.map(p => (
                        <Badge key={p.id} variant="outline" className={`text-[10px] ${p.brand === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                          {p.title}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-white text-sm font-medium">&#8377;{account.total_amount.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-emerald-400 text-sm">&#8377;{account.total_paid.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium ${account.balance > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      &#8377;{account.balance.toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isManager && (
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openPaymentDialog(account); }}
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid={`add-payment-${account.client_id}`}>
                        <IndianRupee className="h-3 w-3 mr-1" /> Add Payment
                      </Button>
                    )}
                  </td>
                </tr>
                {/* Payment History Row */}
                {expandedClient === account.client_id && (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 bg-[#0A0A0A]">
                      <div className="space-y-3">
                        <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Payment History</p>
                        {(clientPayments[account.client_id] || []).length === 0 ? (
                          <p className="text-sm text-zinc-500">No payments recorded yet</p>
                        ) : (
                          <div className="space-y-2">
                            {(clientPayments[account.client_id] || []).map(pay => (
                              <div key={pay.id} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5">
                                <div className="flex items-center gap-3">
                                  <div className={`p-2 rounded ${pay.mode === 'upi' ? 'bg-blue-500/10' : 'bg-emerald-500/10'}`}>
                                    {pay.mode === 'upi' ? <CreditCard className="h-4 w-4 text-blue-400" /> : <IndianRupee className="h-4 w-4 text-emerald-400" />}
                                  </div>
                                  <div>
                                    <p className="text-white text-sm font-medium">&#8377;{pay.amount.toLocaleString('en-IN')}</p>
                                    <p className="text-[11px] text-zinc-500">
                                      {pay.mode.toUpperCase()}
                                      {pay.account ? ` → ${pay.account}` : ''}
                                      {pay.notes ? ` • ${pay.notes}` : ''} | by {pay.created_by_name}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  {pay.screenshot_path && (
                                    <a href={`${API_URL}${pay.screenshot_path}`} target="_blank" rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                                      <Image className="h-3 w-3" /> View
                                    </a>
                                  )}
                                  <span className="text-xs text-zinc-500">
                                    {pay.received_date ? new Date(pay.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <IndianRupee className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No accounts found</p>
            <p className="text-sm mt-1">Projects with amounts will appear here</p>
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedClient?.client_name} — Balance: &#8377;{selectedClient?.balance?.toLocaleString('en-IN')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Amount (&#8377;) *</Label>
              <Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="50000" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Mode *</Label>
              <Select value={paymentForm.mode} onValueChange={v => setPaymentForm({...paymentForm, mode: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="payment-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="cash" className="text-white hover:bg-white/10">Cash</SelectItem>
                  <SelectItem value="upi" className="text-white hover:bg-white/10">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Received In Account</Label>
              <div className="flex gap-1.5 mt-1 flex-wrap">
                {['Shubham', 'Lucky', 'Pankaj'].map(acc => (
                  <button key={acc} type="button"
                    onClick={() => setPaymentForm({...paymentForm, account: acc})}
                    className={`px-3 py-1.5 rounded border text-xs transition-colors ${
                      paymentForm.account === acc
                        ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]'
                        : 'border-white/10 text-zinc-400 hover:border-white/30'
                    }`}
                    data-testid={`pay-account-${acc}`}>{acc}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date</Label>
              <Input type="date" value={paymentForm.received_date} onChange={e => setPaymentForm({...paymentForm, received_date: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
              <Input value={paymentForm.notes} onChange={e => setPaymentForm({...paymentForm, notes: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Optional note" />
            </div>
            {(paymentForm.mode === 'upi' || paymentForm.mode === 'cash') && (
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Screenshot (Optional)</Label>
                <div className="mt-1">
                  <label className="flex items-center gap-2 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md cursor-pointer hover:border-white/20 transition-colors">
                    <Upload className="h-4 w-4 text-zinc-400" />
                    <span className="text-sm text-zinc-400">{screenshotFile ? screenshotFile.name : 'Choose file'}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => setScreenshotFile(e.target.files[0])} data-testid="payment-screenshot" />
                  </label>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleCreatePayment} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-payment-btn">Record Payment</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPage;
